export const Feline_Fundamentals = {
    catTemperaments: {
        title: "Understanding Cat Temperaments",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/catTemperamentsTitle.png",
        body: `
          <section>
            <h3>🐱 Respect the Feline Mind</h3>
            <p>Cats aren’t small dogs. They’re <span class="highlight">independent, emotionally complex</span>, and deeply attuned to their environment. Understanding how they express stress or comfort is essential for safe handling and emotional trust.</p>
          </section>
      
          <section>
            <h4>🧐 Watch Closely: Key Signals</h4>
            <ul>
              <li>➡️ <strong>Tail flicks:</strong> Mild agitation, overstimulation</li>
              <li>👁️ <strong>Dilated pupils:</strong> Stress, arousal, or fear</li>
              <li>🧍‍♀️ <strong>Hiding:</strong> A natural self-regulation strategy — <span class="highlight-yellow">do not disturb</span></li>
              <li>🗣️ <strong>Vocalizations:</strong> Growls, hisses, or yowls = clear boundaries being set</li>
            </ul>
            <p><span class="highlight-blue">Your posture matters:</span> Avoid direct eye contact, don’t corner, and allow cats to approach you.</p>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/catTemperamentsSigns.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>⏳ Let Them Set the Pace</h4>
            <ul>
              <li>🙇 Don’t reach overhead — let them come to your hand at ground level</li>
              <li>💨 Rushing or chasing damages trust instantly</li>
              <li>🧘‍♀️ Stay low, calm, and still — patience builds confidence</li>
            </ul>
          </section>
      
          <section>
            <h4>🧸 Real-Life Scenario</h4>
            <p><em>Pebbles, a shy tabby, has just arrived for boarding. She’s tucked behind her crate. Instead of coaxing, a team member quietly places a fleece blanket over half the crate, leaves a soft hiding cube nearby, and monitors from a distance. By afternoon, Pebbles emerges and stretches, blinking slowly at staff — a sign of growing trust.</em></p>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/catTemperamentsRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">Every cat is different, but all cats need choice and control</span>. When we honor their pace, they begin to trust ours.</p>
          </section>
        `
    },      
    catHealthSafety: {
        title: "Basic Health & Safety for Cats",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/catHealthSafetyTitle.png",
        body: `
          <section>
            <h3>🐱 Core Principles of Feline Safety</h3>
            <p>
              Cats are subtle communicators, sensitive to noise, scent, and touch. 
              Health and safety for our feline guests requires a <span class="highlight">quiet presence, proactive awareness, and gentle handling</span>. 
              Prevention is everything.
            </p>
          </section>
      
          <section>
            <h4>🩺 Daily Health Observations</h4>
            <ul>
              <li>👁️ <strong>Eyes:</strong> Clear, no discharge or redness</li>
              <li>👃 <strong>Nose:</strong> No sneezing, crust, or nasal drip</li>
              <li>🐾 <strong>Body check:</strong> Look for limping, wounds, or excessive licking</li>
              <li>🍽️ <strong>Appetite & Elimination:</strong> Monitor food intake and litter box usage for signs of illness</li>
            </ul>
            <p>
              <span class="highlight-yellow">Note any changes and report them immediately</span> — cats often hide discomfort.
            </p>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/catHealthSafetyProtocols.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🧼 Hygiene & Safety Protocols</h4>
            <ul>
              <li>🧴 <strong>Clean spaces daily:</strong> Litter, bedding, bowls, and cubbies must be <span class="highlight">clean, dry, and scent-free</span>.</li>
              <li>🧤 <strong>Use gloves</strong> when handling waste or administering meds.</li>
              <li>🪟 <strong>Secure enclosures:</strong> Double-check gates and doors — <strong>cats are escape artists</strong>.</li>
              <li>🎧 <strong>Minimize noise:</strong> No sudden loud sounds. Keep stress low with a quiet, calm tone.</li>
            </ul>
          </section>
      
          <section>
            <h4>🛑 When to Flag a Supervisor</h4>
            <p>If you notice any of the following, <strong>pause all interaction and inform a lead immediately:</strong></p>
            <ul>
              <li>🩸 Bleeding or limping</li>
              <li>🤢 Vomiting or diarrhea</li>
              <li>😾 Unusual aggression or hiding</li>
              <li>🌡️ Lethargy or rapid breathing</li>
            </ul>
            <p><span class="highlight-red">Never guess — always report.</span></p>
          </section>
      
            <section>
                <h4>💡 Final Thought</h4>
                <p>
                    In feline care, <span class="highlight">small signs speak volumes</span>. 
                    Your calm energy, clean habits, and careful eyes are what keep our cats safe. 
                    Trust your instincts, follow the protocol, and when in doubt — report, log, and ask.
                    <strong>At Pawsible, cats rely on our quiet strength.</strong>
                </p>
            </section>
        `
    },      
    catBodyLanguage: {
        title: "Reading Cat Body Language",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/catBodyLanguageTitle.png",
        body: `
          <section>
            <h3>👁️ The Quiet Language of Cats</h3>
            <p>
              Cats speak volumes — just not with words. 
              <span class="highlight">Body language is their first line of communication</span>, and recognizing subtle cues helps us meet their needs without overwhelming them.
            </p>
          </section>
      
          <section>
            <h4>🔍 Key Body Language Cues</h4>
            <ul>
              <li>🐾 <strong>Tail Position:</strong> 
                <ul>
                  <li>Up = confidence</li>
                  <li>Low or tucked = fear or anxiety</li>
                  <li>Flicking = overstimulated or annoyed</li>
                </ul>
              </li>
              <li>👁️ <strong>Eyes:</strong> 
                <ul>
                  <li>Soft blinks = trust</li>
                  <li>Wide pupils = stress or excitement</li>
                  <li>Staring = challenge or discomfort</li>
                </ul>
              </li>
              <li>👂 <strong>Ears:</strong> 
                <ul>
                  <li>Forward = alert or curious</li>
                  <li>Sideways = unsure</li>
                  <li>Flattened = fear, stress, or aggression</li>
                </ul>
              </li>
              <li>🧍‍♂️ <strong>Posture:</strong> 
                <ul>
                  <li>Loafed = relaxed</li>
                  <li>Crouched = defensive</li>
                  <li>Arched back = fear or warning</li>
                </ul>
              </li>
            </ul>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/catBodyLanguageCues.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>📈 What to Watch For</h4>
            <p>
              <span class="highlight-yellow">Changes in posture, vocalization, or interaction style</span> often signal discomfort or illness. Pay attention to small shifts — they matter.
            </p>
          </section>
      
          <section>
            <h4>🧘‍♀️ Responding Mindfully</h4>
            <ul>
              <li>Approach from the side, not head-on</li>
              <li>Let them come to you — don’t reach first</li>
              <li>Use calm movements, soft voice, and offer one hand at floor level</li>
            </ul>
            <p><span class="highlight-blue">Respect is the fastest route to trust.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight-green">Cats are subtle, not silent.</span> 
              Learning their language helps us deliver safer care and deeper connection every day.
            </p>
          </section>
        `
    },
    catSocialization: {
        title: "Feline Socialization & Handling",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/catSocializationTitle.png",
        body: `
          <section>
            <h3>🤝 Building Trust, Not Tolerance</h3>
            <p>
              Socializing a cat isn’t about forcing affection — it’s about <span class="highlight">creating consistent, low-pressure opportunities for connection</span>. Gentle exposure and consent-based handling build lasting trust.
            </p>
          </section>
      
          <section>
            <h4>🐾 Socialization Best Practices (Human Interaction)</h4>
            <ul>
              <li>🧘‍♀️ <strong>Be present, not pushy:</strong> Sit nearby and allow the cat to initiate</li>
              <li>👋 <strong>Offer one hand, palm down:</strong> Let the cat sniff before touching</li>
              <li>🪑 <strong>Stay at their level:</strong> Sit on the floor or kneel to reduce perceived threat</li>
              <li>🎯 <strong>Short, positive sessions:</strong> 3–5 minutes of interaction, then step away</li>
              <li>🧸 <strong>Use enrichment:</strong> Wand toys, treat puzzles, and boxes can create shared joy</li>
            </ul>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/catSocializationBestPractice.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🐈‍⬛ Feline-to-Feline Socialization</h4>
            <p><span class="highlight-blue">Cats are not naturally pack animals</span>, but they can coexist and even bond — if introduced and monitored thoughtfully.</p>
            <ul>
              <li>🔎 <strong>Start with Scent:</strong> Let cats smell each other’s bedding before face-to-face meetings</li>
              <li>🚪 <strong>Barrier Observation:</strong> Use crates, pens, or glass dividers so cats can see but not access each other initially</li>
              <li>⏳ <strong>Slow Introductions:</strong> 5–10 minutes max, with lots of vertical escape options</li>
              <li>🍗 <strong>Positive Association:</strong> Feed treats on opposite sides of the room to associate proximity with reward</li>
              <li>🛑 <strong>Red Flags:</strong> Staring, flattened ears, tail lashing, growling — pause immediately</li>
            </ul>
            <p><span class="highlight-yellow">Some cats prefer solo time — that’s okay.</span> Coexistence is success even without play.</p>
          </section>
      
          <section>
            <h4>🤲 Handling Guidelines</h4>
            <ul>
              <li>💨 <strong>Don’t rush:</strong> If the cat resists, pause and try again later</li>
              <li>🐱 <strong>Secure but gentle:</strong> Always support the body, especially the back legs</li>
              <li>🧤 <strong>Towel wraps (if needed):</strong> For safety only, not convenience</li>
              <li>⛔ <strong>Never scruff a cat at Pawsible</strong></li>
            </ul>
            <p><span class="highlight-red">Consent-based care is non-negotiable. Forcing contact breaks trust.</span></p>
          </section>
      
          <section>
            <h4>🐈 Scenario: Cat-to-Cat Comfort</h4>
            <p>
              <em>Two new cats, Willow and Mochi, are introduced for social time. A team member places their condos side by side and watches for calm body language. Over three days, they begin eating near each other and mirroring behavior. By Day 5, they nap side by side in a sun spot — no contact, but full comfort.</em>
            </p>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/catSocializationCatToCat.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight-green">Cat socialization is about trust — with humans *and* each other.</span> Let them set the pace. Connection follows comfort.
            </p>
          </section>
        `
    },      
    catEmergencies: {
        title: "Feline Emergency Response Protocols",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/catEmergencyTitle.png",
        body: `
          <section>
            <h3>🚨 Stay Calm, Move Smart</h3>
            <p>
              Feline emergencies require <span class="highlight">quiet confidence, not chaos</span>. Cats are highly sensitive to energy — so when something feels off, your calm and fast response can make all the difference.
            </p>
          </section>
      
          <section>
            <h4>🩺 Signs of Distress</h4>
            <ul>
              <li>😮‍💨 Rapid or open-mouth breathing</li>
              <li>🩸 Bleeding, limping, visible wounds</li>
              <li>🧍‍♀️ Collapse, lethargy, or sudden freezing behavior</li>
              <li>🐈‍⬛ Vocalization or aggression that is out of character</li>
              <li>🚽 Straining to urinate or signs of pain while eliminating</li>
            </ul>
            <p><span class="highlight-yellow">If you see any of these — alert a supervisor immediately.</span></p>
          </section>
      
          <section>
            <h4>🧰 Immediate Protocols</h4>
            <ul>
              <li>🗣️ <strong>Stay quiet:</strong> No loud calls or fast movements — use walkie or text to notify staff</li>
              <li>🛑 <strong>Block off the area:</strong> Prevent access by other animals</li>
              <li>🐾 <strong>Contain the cat:</strong> Gently wrap in a towel or place in secure crate if possible</li>
              <li>📞 <strong>Alert Supervisor + Log It:</strong> Use the dashboard or incident form immediately after stabilization</li>
            </ul>
          </section>
      
          <section>
            <h4>🏥 When to Call a Vet Immediately</h4>
            <ul>
              <li>🫁 Trouble breathing or choking</li>
              <li>🧠 Seizures or loss of consciousness</li>
              <li>🩸 Uncontrolled bleeding</li>
              <li>🐾 Inability to walk, sudden collapse, or dragging limbs</li>
            </ul>
            <p><span class="highlight-red">In these cases, escalate without delay.</span> Safety always comes first.</p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">Feline emergencies require gentleness and urgency in equal measure.</span> Stay clear-headed, communicate clearly, and lean on the team.</p>
          </section>
        `
    }      
};